/** Foot dust while pacing, and muzzle smoke after a shot. */

import { FRAME_H } from "./sprites";

export type ParticleKind = "dust" | "smoke";

export interface DustParticle {
  kind: ParticleKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Seconds remaining. */
  life: number;
  maxLife: number;
  size: number;
  /** Optional size growth per second (smoke puffs out). */
  grow: number;
  spin: number;
  angle: number;
  /** Draw above fighters (costume burst); foot dust stays underneath. */
  above: boolean;
}

/** White streak for Cowboy Dummy pop VFX. */
export interface PopStreak {
  x: number;
  y: number;
  vx: number;
  vy: number;
  length: number;
  life: number;
  maxLife: number;
}

const MAX_PARTICLES = 80;
const PARTICLE_SIZE = 27;

export class DustField {
  private particles: DustParticle[] = [];
  private streaks: PopStreak[] = [];

  clear(): void {
    this.particles.length = 0;
    this.streaks.length = 0;
  }

  get all(): readonly DustParticle[] {
    return this.particles;
  }

  get popStreaks(): readonly PopStreak[] {
    return this.streaks;
  }

  /** Puff at a fighter's feet. `dirX` is -1 (moving left) or 1 (moving right). */
  spawnStepPuff(x: number, y: number, dirX: number): void {
    const count = 2 + Math.floor(Math.random() * 2);
    for (let i = 0; i < count; i++) {
      if (this.particles.length >= MAX_PARTICLES) break;
      const life = 0.45 + Math.random() * 0.3;
      this.particles.push({
        kind: "dust",
        x: x + (Math.random() - 0.5) * 10,
        y: y + FRAME_H / 2 - 4 + (Math.random() - 0.5) * 4,
        // Drift opposite the direction of travel, with a small upward lift
        vx: -dirX * (8 + Math.random() * 16) + (Math.random() - 0.5) * 8,
        vy: -(6 + Math.random() * 12),
        life,
        maxLife: life,
        size: PARTICLE_SIZE * (0.4 + Math.random() * 0.35),
        grow: 0,
        spin: (Math.random() - 0.5) * 1.6,
        angle: Math.random() * Math.PI * 2,
        above: false,
      });
    }
  }

  /** Big costume-change burst (Cactus Suit at countdown start). */
  spawnCostumePuff(x: number, y: number): void {
    const count = 22 + Math.floor(Math.random() * 6);
    for (let i = 0; i < count; i++) {
      if (this.particles.length >= MAX_PARTICLES) break;
      const life = 0.55 + Math.random() * 0.4;
      const dir = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 140;
      const along = (Math.random() - 0.5) * 28;
      this.particles.push({
        kind: "dust",
        x: x + Math.cos(dir) * along * 0.35 + (Math.random() - 0.5) * 12,
        y: y + Math.sin(dir) * along * 0.35 + (Math.random() - 0.5) * 16,
        vx: Math.cos(dir) * speed,
        vy: Math.sin(dir) * speed * 0.75 - (20 + Math.random() * 50),
        life,
        maxLife: life,
        size: PARTICLE_SIZE * (0.7 + Math.random() * 0.85),
        grow: 8 + Math.random() * 14,
        spin: (Math.random() - 0.5) * 2.4,
        angle: Math.random() * Math.PI * 2,
        above: true,
      });
    }
  }

  /** Soft puff at the muzzle right after a shot. `facing` is aim radians. */
  spawnMuzzleSmoke(x: number, y: number, facing: number): void {
    const cos = Math.cos(facing);
    const sin = Math.sin(facing);
    const count = 5 + Math.floor(Math.random() * 2);
    for (let i = 0; i < count; i++) {
      if (this.particles.length >= MAX_PARTICLES) break;
      const life = 0.7 + Math.random() * 0.45;
      // Fan out from the muzzle so they don't stack on one spot
      const spread = ((i / (count - 1)) - 0.5) * 0.9 + (Math.random() - 0.5) * 0.35;
      const dir = facing + spread;
      const dcos = Math.cos(dir);
      const dsin = Math.sin(dir);
      const speed = 70 + Math.random() * 90;
      const along = 6 + Math.random() * 18;
      const side = (Math.random() - 0.5) * 22;
      this.particles.push({
        kind: "smoke",
        x: x + cos * along - sin * side,
        y: y + sin * along + cos * side,
        vx: dcos * speed + (Math.random() - 0.5) * 20,
        vy: dsin * speed - (25 + Math.random() * 40),
        life,
        maxLife: life,
        size: PARTICLE_SIZE * (0.55 + Math.random() * 0.45),
        grow: 14 + Math.random() * 18,
        spin: (Math.random() - 0.5) * 1.2,
        angle: Math.random() * Math.PI * 2,
        above: false,
      });
    }
  }

  /** White lines shooting outward when a Cowboy Dummy is shot. */
  spawnDummyPop(x: number, y: number): void {
    const count = 14;
    for (let i = 0; i < count; i++) {
      const dir = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.35;
      const speed = 320 + Math.random() * 180;
      const life = 0.14 + Math.random() * 0.08;
      this.streaks.push({
        x,
        y,
        vx: Math.cos(dir) * speed,
        vy: Math.sin(dir) * speed,
        length: 6 + Math.random() * 8,
        life,
        maxLife: life,
      });
    }
  }

  update(dt: number): void {
    for (const p of this.particles) {
      p.life -= dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.size += p.grow * dt;
      if (p.kind === "smoke") {
        // Light drag so they coast out before hanging
        p.vx *= 1 - Math.min(1, 0.55 * dt);
        p.vy *= 1 - Math.min(1, 0.35 * dt);
        p.vy -= 10 * dt;
      } else {
        // Dust settles: slow drift and gentle sink as it fades
        p.vx *= 1 - Math.min(1, 2.4 * dt);
        p.vy += 14 * dt;
      }
      p.angle += p.spin * dt;
    }
    this.particles = this.particles.filter((p) => p.life > 0);

    for (const s of this.streaks) {
      s.life -= dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
    }
    this.streaks = this.streaks.filter((s) => s.life > 0);
  }
}
